/**
 * Banker-Recon LO Profitability — routing-aware, signed, report-only.
 *
 * Uses the same Net-LO-Locked waterfall inputs as Product & Pricing, while
 * preserving source precision for finalized accounting instead of converting
 * the three-decimal displayed price back to dollars. Each routable
 * credit can be aimed at the LO's **profitability** scorecard (signed, may go
 * negative — it just carries into the My Commission sum), the LO's **base
 * compensation** check (where it becomes an `lo_deduction` line item handled by
 * `computeBankedRecon`), or (for lender credit) the **company**, where that
 * adjustment has no LO scorecard or payout effect. There is no cap, lock, or
 * pool here — this is pure math.
 *
 * Invariants:
 *   - `gross` is toggle-independent "clean loan profitability": the pricing gain
 *     with lender credit / cure zeroed AND the **applied LO-comp-reduction release
 *     added back** (`locrPoints = appliedRelease`). The release is baked into the
 *     buy price, so adding it back yields the loan's own pricing position before
 *     any give-back — matching Product & Pricing's underlying waterfall. LOCR/cure
 *     then show as separate allocatable deduction lines.
 *   - Company concessions are EXCLUDED from `gross` (they are the company's
 *     contribution, not the loan's own pricing) and surfaced as a non-routable
 *     `company_concession` component whose effect is derived through the SAME
 *     unrounded waterfall (delta of the clean-gross evaluation with vs without
 *     `concessionPoints`) — never an independent points×loan rounding. The
 *     additive bridge reads: Gross + Company Concession − credits/comp = Net.
 *   - `net` is computed by feeding the routed components back through the SAME
 *     unrounded `computeNetLoLocked` waterfall (credits as points), so source
 *     cents carry through even when the displayed price rounds to three decimals.
 *   - `gross + Σ component.effectAmount === net` — any deterministic cent
 *     residual from separately rounded bridge lines is assigned to the last
 *     non-zero component so the displayed/stored breakdown always reconciles.
 *   - For loans with no built-in "LO Compensation" margin (`loCompBps <= 0.0005`),
 *     the partner's base comp is paid from the pricing gain: `net` drops by
 *     `loPayoutBase` (and rises by borrower-paid comp), shown as separate lines.
 *   - For loans with built-in LO compensation, the signed difference between
 *     built-in margin and the actual plan payout adjusts only the LO Profitability
 *     scorecard. The company P&L already carries the full payout independently.
 *
 * No React, no hooks, no I/O — keep it that way.
 */

import {
  computeLoProfitability,
  computeNetLoLocked,
  dollarsToPointsAt,
  type NetLoLockedInput
} from '@/utils/pricing/netLoLocked';

export type ProfitabilityRoutingTarget = 'base_comp' | 'lo_profitability' | 'company';

/** The three routable credits, in canonical (display + reconcile) order. */
export const ROUTABLE_CREDIT_TYPES = [
  'lender_credit',
  'cure_applied_lender_credit',
  'lo_comp_reduction'
] as const;

export type RoutableCreditType = (typeof ROUTABLE_CREDIT_TYPES)[number];

export const ROUTABLE_CREDIT_LABELS: Record<RoutableCreditType, string> = {
  lender_credit: 'Lender Credit',
  cure_applied_lender_credit: 'Cure Applied to Lender Credit',
  lo_comp_reduction: 'LO Comp Reduction'
};

/** Default routing per credit: costs erode profitability; the comp give-back stays on the check. */
export const DEFAULT_ROUTING_TARGET: Record<RoutableCreditType, ProfitabilityRoutingTarget> = {
  lender_credit: 'lo_profitability',
  cure_applied_lender_credit: 'lo_profitability',
  lo_comp_reduction: 'base_comp'
};

export const PROFITABILITY_ROUTING_TARGET_LABELS: Record<ProfitabilityRoutingTarget, string> = {
  lo_profitability: 'LO Profitability',
  base_comp: 'Base Comp',
  company: 'N/A (Company)'
};

/** Company routing is intentionally limited to the lender credit requested by the operator. */
export function isAllowedProfitabilityRoutingTarget(
  type: RoutableCreditType,
  target: unknown
): target is ProfitabilityRoutingTarget {
  return target === 'lo_profitability'
    || target === 'base_comp'
    || (type === 'lender_credit' && target === 'company');
}

export interface RoutableCredit {
  target: ProfitabilityRoutingTarget;
  /** User-adjustable dollar amount routed (≤ `sourceAmount`). */
  amount: number;
  /** The credit's full available dollar amount on the loan. */
  sourceAmount: number;
}

/** Recon-level persisted routing (`banker_recons.profitability_routing`). */
export type ProfitabilityRouting = Partial<Record<RoutableCreditType, RoutableCredit>>;

export interface ReconLoProfitabilityInput {
  /** `rate_lock_total_buy_price` — price locked after LLPAs. */
  basePriceLocked: number;
  /** LO discount points % (NEWHUD.X1150). */
  discountPoints: number;
  /** Σ active (non-legacy) concession points. */
  concessionPoints: number;
  /**
   * Σ active (non-legacy) ENTERED concession dollars (`lendwise_concessions.amount`).
   * Audit context only — becomes the component's `sourceAmount`; pricing math uses
   * `concessionPoints` through the waterfall. Omitted ⇒ points-derived fallback.
   */
  concessionDollarsEntered?: number;
  loanAmount: number;
  routing: ProfitabilityRouting;
  /**
   * Built-in "LO Compensation" ProfitMargin bps (`extractEncompassLoCompBps`).
   * Caps the applied LOCR release added back into clean gross (P&P parity), and
   * — when `<= 0.0005` — flags Case 2 (no built-in margin → subtract base comp).
   * Omitted/undefined ⇒ no clean-gross add-back and NOT Case 2.
   */
  loCompBps?: number;
  /** Borrower-paid comp dollars (`origination_fee_percent × loanAmount / 100`). Case 2 only. */
  borrowerPaidCompDollars?: number;
  /** LO base compensation before `lo_deduction` rows (`computeBankedRecon().loPayoutBase`). Case 2 + variance. */
  loPayoutBase?: number;
}

/** Non-routable profitability lines. Display-only, never in `ProfitabilityRouting`. */
export type ProfitabilityComponentType =
  | RoutableCreditType
  | 'lo_compensation'
  | 'lo_compensation_variance'
  | 'borrower_paid_comp'
  | 'company_concession';

export interface LoCompensationVarianceBasis {
  planAmount: number;
  planPoints: number;
  builtInAmount: number;
  builtInPoints: number;
  varianceAmount: number;
  variancePoints: number;
}

export interface ReconLoProfitabilityComponent {
  type: ProfitabilityComponentType;
  label: string;
  /** Routing target for routable credits; `null` for non-routable disclosure lines. */
  target: ProfitabilityRoutingTarget | null;
  amount: number;
  sourceAmount: number;
  /** Magnitude in price points (1 point = 1% of loan). */
  points: number;
  /** Signed dollar contribution to net (negative = cost; positive = add-back). */
  effectAmount: number;
  /** Audit bridge for signed built-in-versus-plan LO compensation variance. */
  varianceBasis?: LoCompensationVarianceBasis;
}

/** Type guard: is this component routable (has a Profitability⇄Base-Comp toggle)? */
export function isRoutableCreditType(type: ProfitabilityComponentType): type is RoutableCreditType {
  return (ROUTABLE_CREDIT_TYPES as readonly string[]).includes(type);
}

export interface ReconLoProfitabilityResult {
  /** Pure pricing gain, all routable credits zeroed (stable across toggles). */
  gross: number;
  /** Signed net after routing — may be negative (carries into the My Commission sum). */
  net: number;
  components: ReconLoProfitabilityComponent[];
}

/** Cent rounding that matches `loCompReduction.roundToCents` (half-up, EPSILON-guarded). */
export const roundToCents = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const profitRoutedAmount = (credit: RoutableCredit | undefined): number =>
  credit && credit.target === 'lo_profitability' ? credit.amount : 0;

const baseCompRoutedAmount = (credit: RoutableCredit | undefined): number =>
  credit && credit.target === 'base_comp' ? credit.amount : 0;

/** Banker Recon finalization uses source precision, not the three-decimal UI price. */
const computeExactLoProfitDollars = (input: NetLoLockedInput): number => {
  const result = computeNetLoLocked(input);
  return computeLoProfitability(result.netLoLocked, input.loanAmount);
};

/**
 * Compute the recon's gross/net LO Profitability and the signed component
 * breakdown from the loan's pricing inputs + per-credit routing.
 */
export function computeReconLoProfitability(
  input: ReconLoProfitabilityInput
): ReconLoProfitabilityResult {
  const {
    basePriceLocked, discountPoints, concessionPoints, concessionDollarsEntered,
    loanAmount, routing,
    loCompBps, borrowerPaidCompDollars = 0, loPayoutBase = 0
  } = input;

  // Applied LO-comp release (= the "margin released to borrower path") that is
  // baked into the buy price. Cap at the built-in LO-comp margin exactly like
  // P&P (`appliedReductionBps`). A missing loCompBps ⇒ no add-back (never
  // Math.min(…, NaN)); Case 2 (margin ~0) ⇒ cap 0 ⇒ no add-back.
  const locrSource = routing.lo_comp_reduction?.sourceAmount ?? 0;
  const hasLoCompMargin = Number.isFinite(loCompBps);
  const marginCapDollars = hasLoCompMargin ? (loCompBps as number) * loanAmount / 10000 : 0;
  const appliedRelease = hasLoCompMargin ? Math.max(0, Math.min(locrSource, marginCapDollars)) : 0;

  // Clean gross — the loan's OWN pricing profitability with the applied LOCR
  // release added back (toggle-independent). Lender credit / cure stay zeroed,
  // and company concessions are excluded (they are surfaced as their own line).
  const cleanGrossInputs = {
    basePriceLocked,
    discountPoints,
    concessionPoints: 0,
    locrPoints: dollarsToPointsAt(loanAmount, appliedRelease),
    lenderCreditPoints: 0,
    curePoints: 0,
    loanAmount
  };
  const cleanGrossDollars = computeExactLoProfitDollars(cleanGrossInputs);
  const gross = roundToCents(cleanGrossDollars);

  // Company-concession contribution — the delta of the SAME clean-gross-shaped
  // waterfall evaluated with vs without concession points, so the line is
  // consistent with `net` rather than independently derived.
  const concessionEffect = concessionPoints > 0
    ? roundToCents(
      computeExactLoProfitDollars({ ...cleanGrossInputs, concessionPoints })
        - cleanGrossDollars
    )
    : 0;

  // Base routing net — unchanged from the original routing model. Profit-routed
  // lender credit / cure subtract; the base-comp-routed LOCR is added back
  // through the SAME waterfall. Exact Case-2 and Case-1 variance effects are
  // appended below after any cent residual has been assigned.
  const lenderCreditProfit = profitRoutedAmount(routing.lender_credit);
  const cureProfit = profitRoutedAmount(routing.cure_applied_lender_credit);
  const locrBaseComp = baseCompRoutedAmount(routing.lo_comp_reduction);

  let net = roundToCents(
    computeExactLoProfitDollars({
      basePriceLocked,
      discountPoints,
      concessionPoints,
      locrPoints: dollarsToPointsAt(loanAmount, locrBaseComp),
      lenderCreditPoints: dollarsToPointsAt(loanAmount, lenderCreditProfit),
      curePoints: dollarsToPointsAt(loanAmount, cureProfit),
      loanAmount
    })
  );

  // Explicit per-line effects (NOT a catch-all remainder, so cent drift can't
  // be mis-attributed to the LOCR):
  //   lender_credit / cure: profitability → −routed, base_comp → 0.
  //   lo_comp_reduction:    roundToCents(baseCompRouted − appliedRelease) ⇒
  //                         full base-comp → 0 (LO funds the release; clean gross
  //                         already added it back; it IS deducted from payout),
  //                         partial base-comp → routed − applied,
  //                         profit-routed → −appliedRelease (company absorbs it).
  const components: ReconLoProfitabilityComponent[] = [];

  // Company concession — first on the bridge (Gross → +Concession → credits →
  // Case-2 lines → Net). Non-routable disclosure of the company's contribution:
  // `amount`/`effectAmount` = waterfall-derived pricing impact; `sourceAmount` =
  // entered dollars (audit context); positive effect (add-back, company-funded).
  if (concessionPoints > 0) {
    components.push({
      type: 'company_concession',
      label: 'Company Concession',
      target: null,
      amount: concessionEffect,
      sourceAmount: concessionDollarsEntered ?? concessionEffect,
      points: concessionPoints,
      effectAmount: concessionEffect
    });
  }

  for (const type of ROUTABLE_CREDIT_TYPES) {
    const credit = routing[type];
    if (!credit) {
      continue;
    }
    let effectAmount = 0;
    if (type === 'lo_comp_reduction') {
      effectAmount = roundToCents(baseCompRoutedAmount(credit) - appliedRelease);
    } else {
      effectAmount = credit.target === 'lo_profitability' ? -roundToCents(credit.amount) : 0;
    }
    components.push({
      type,
      label: ROUTABLE_CREDIT_LABELS[type],
      target: credit.target,
      amount: credit.amount,
      sourceAmount: credit.sourceAmount,
      points: dollarsToPointsAt(loanAmount, credit.amount),
      effectAmount
    });
  }

  // Reconcile: gross + Σ effectAmount must equal net. Separately rounded bridge
  // lines can drift by a cent, so assign the
  // deterministic residual to the last non-zero routable component — BEFORE
  // appending the exact Case-2 / LO-comp-variance lines below.
  const summedEffect = roundToCents(components.reduce((s, c) => s + c.effectAmount, 0));
  const residual = roundToCents(net - gross - summedEffect);
  if (residual !== 0) {
    let target: ReconLoProfitabilityComponent | undefined;
    for (let i = components.length - 1; i >= 0; i--) {
      if (components[i].effectAmount !== 0) {
        target = components[i];
        break;
      }
    }
    if (target) {
      target.effectAmount = roundToCents(target.effectAmount + residual);
    } else if (components.length > 0) {
      const last = components[components.length - 1];
      last.effectAmount = roundToCents(last.effectAmount + residual);
    }
  }

  // Case 2 — no built-in "LO Compensation" margin: the partner's base comp is
  // paid from the pricing gain, so subtract it (and add back borrower-paid comp).
  // `loPayoutBase` is GROSS base comp; base-comp-routed deductions already flow
  // through `net`. Reimbursables are pass-through (NOT subtracted). Signed (no floor).
  const isCase2 = Number.isFinite(loCompBps) && (loCompBps as number) <= 0.0005;
  if (isCase2) {
    const bpc = roundToCents(borrowerPaidCompDollars);
    const comp = roundToCents(loPayoutBase);
    net = roundToCents(net + bpc - comp);
    if (bpc !== 0) {
      components.push({
        type: 'borrower_paid_comp',
        label: 'Borrower-Paid Comp',
        target: null,
        amount: bpc,
        sourceAmount: bpc,
        points: dollarsToPointsAt(loanAmount, bpc),
        effectAmount: bpc
      });
    }
    if (comp !== 0) {
      components.push({
        type: 'lo_compensation',
        label: 'LO Compensation',
        target: null,
        amount: comp,
        sourceAmount: comp,
        points: dollarsToPointsAt(loanAmount, comp),
        effectAmount: -comp
      });
    }
  }

  // Case 1 — built-in LO compensation margin exists. The company P&L already
  // subtracts the full plan payout; this signed bridge records only the
  // built-in-versus-plan difference on the LO Profitability scorecard.
  // A non-positive plan is the untouched/default state, so fail closed instead
  // of briefly crediting the entire built-in margin on a fresh draft.
  const planAmount = Number.isFinite(loPayoutBase) ? roundToCents(loPayoutBase) : 0;
  const hasVarianceBasis = Number.isFinite(loCompBps)
    && (loCompBps as number) > 0.0005
    && planAmount > 0
    && Number.isFinite(marginCapDollars);
  if (hasVarianceBasis) {
    const builtInAmount = roundToCents(marginCapDollars);
    const varianceAmount = roundToCents(builtInAmount - planAmount);
    if (varianceAmount !== 0) {
      const varianceMagnitude = Math.abs(varianceAmount);
      net = roundToCents(net + varianceAmount);
      components.push({
        type: 'lo_compensation_variance',
        label: 'LO Compensation Variance',
        target: null,
        amount: varianceMagnitude,
        sourceAmount: planAmount,
        points: dollarsToPointsAt(loanAmount, varianceMagnitude),
        effectAmount: varianceAmount,
        varianceBasis: {
          planAmount,
          planPoints: dollarsToPointsAt(loanAmount, planAmount),
          builtInAmount,
          builtInPoints: dollarsToPointsAt(loanAmount, builtInAmount),
          varianceAmount,
          variancePoints: dollarsToPointsAt(loanAmount, varianceAmount)
        }
      });
    }
  }

  return { gross, net, components };
}
